import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getWebApp } from '@/lib/telegram';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle2, XCircle, Clock, ArrowDownCircle, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { StarIcon } from '@/components/ui/star-icon';
import { intentLabel as labelForIntent, sourceLabel } from '@/lib/providers';
import { formatNumber, shortDateTime } from '@/lib/date';
import { formatMinor } from '@/lib/currency';
import { Skeleton, SkeletonCard, SkeletonLine } from '@/components/ui/skeleton';

export const Route = createFileRoute('/payments')({
  component: PaymentsHistory,
});

function StatusBadge({ status, incoming }: { status: string; incoming: boolean }) {
  if (status === 'refunded') return <ArrowDownCircle className="w-5 h-5 text-accent" />;
  if (status === 'pending') return <Clock className="w-5 h-5 text-[#f0b04e]" />;
  // Приход и расход различаем стрелкой, а не одной галочкой на всё: в объединённой
  // ленте рядом стоят «купил» и «начислили», и одинаковый значок их сливает.
  if (status === 'paid') {
    return incoming ? (
      <ArrowDownCircle className="w-5 h-5 text-[#5fe3ad]" />
    ) : (
      <CheckCircle2 className="w-5 h-5 text-[#5fe3ad]" />
    );
  }
  return <XCircle className="w-5 h-5 text-[#f0656a]" />;
}

const STATUS_COLOR: Record<string, string> = {
  paid: 'text-[#5fe3ad]',
  pending: 'text-[#f0b04e]',
  refunded: 'text-accent',
};

/** Ключи подписей статуса счёта. Незнакомый статус показываем как «не прошёл». */
const STATUS_LABEL: Record<string, string> = {
  paid: 'Payment status paid',
  pending: 'Payment status pending',
  failed: 'Payment status failed',
  cancelled: 'Payment status cancelled',
  expired: 'Payment status expired',
  refunded: 'Payment status refunded',
};

function PaymentsHistory() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ['payments-history'],
    queryFn: () => api.paymentsHistory(),
  });

  useEffect(() => {
    const tg = getWebApp();
    if (!tg?.BackButton) return;
    tg.BackButton.show();
    const onBack = () => navigate({ to: '/profile' });
    tg.BackButton.onClick(onBack);
    return () => tg.BackButton.offClick(onBack);
  }, [navigate]);

  useEffect(() => {
    const tg = getWebApp();
    tg?.MainButton.hide();
  }, []);

  const items = data?.items ?? [];

  return (
    <div className="p-4 space-y-3 pb-24">
      <h2 className="text-[25px] font-extrabold tracking-[-0.8px] px-2 pt-2 text-foreground">{t('Payments history')}</h2>
      {isPending ? (
        <PaymentsSkeleton />
      ) : isError ? (
        <Card>
          <CardContent className="p-6 text-center space-y-3 flex flex-col items-center">
            <AlertTriangle className="w-7 h-7 text-[#f0656a]" />
            <div className="text-sm text-[#8a8c93]">
              {(error as Error)?.message || t('Payments load failed')}
            </div>
            <button
              type="button"
              onClick={() => refetch()}
              className="calm-cta px-4 py-2 rounded-lg text-xs font-bold"
            >
              {t('Retry')}
            </button>
          </CardContent>
        </Card>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-[#8a8c93]">
            {t('Payments empty')}
          </CardContent>
        </Card>
      ) : (
        items.map((p) => {
          const intentLabel = labelForIntent(p.intent_type, p.signed, p.source);
          // XTR (Telegram Stars) — целые звёзды, без деления на 100. Рубли с копейками
          // до двух знаков: «349,50 ₽», а не «349,5 ₽».
          const amount =
            p.currency === 'XTR' ? (
              <span className="inline-flex items-center gap-0.5 whitespace-nowrap">
                {formatNumber(p.amount_display_minor, i18n.language)}
                <StarIcon className="h-3.5 w-3.5 text-[#f5b400]" aria-label="⭐" />
              </span>
            ) : (
              formatMinor(p.amount_display_minor, p.currency, i18n.language)
            );
          const succeeded = p.status === 'paid' || p.status === 'pending' || p.status === 'refunded';
          const incoming = p.signed > 0;
          return (
            <Card key={p.id} className={cn(!succeeded && 'opacity-[0.72]')}>
              <CardContent className="p-4 flex items-center gap-3">
                <StatusBadge status={p.status} incoming={incoming} />
                <div className="flex-1 min-w-0">
                  {/* Подпись переносится, а не режется: «Возврат заявки на вывод» и
                      «Перенос баланса при объединении…» иначе обрывались на полуслове. */}
                  <div className="font-semibold leading-snug text-foreground [overflow-wrap:anywhere]">{intentLabel}</div>
                  <div className="text-[11px] text-[#8a8c93]" style={{ fontFamily: 'var(--font-mono)' }}>
                    {sourceLabel(p.source, p.provider)} · {shortDateTime(p.created_at, i18n.language)}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  {/* Знак обязателен: начисление бонуса без плюса читается как списание. */}
                  <div className={cn('font-bold tabular whitespace-nowrap', incoming ? 'text-[#5fe3ad]' : 'text-foreground')}>
                    {incoming ? '+' : '−'}
                    {amount}
                  </div>
                  {/* У операции по балансу статуса не бывает: проводка либо есть, либо
                      её нет. Плашка «PAID» на каждой строке была бы шумом. */}
                  {p.source === 'payment' && (
                    <div className={cn('text-[10px] font-bold whitespace-nowrap', STATUS_COLOR[p.status] ?? 'text-[#f0656a]')}>
                      {t(STATUS_LABEL[p.status] ?? 'Payment status failed')}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })
      )}
    </div>
  );
}

/** Повторяет карточку платежа: иконка статуса, назначение с провайдером, сумма. */
function PaymentsSkeleton() {
  return (
    <div className="space-y-3">
      {[0, 1, 2].map((i) => (
        <SkeletonCard key={i} className="rounded-[24px] min-h-0 p-4 flex items-center gap-3">
          <Skeleton className="w-5 h-5 rounded-full shrink-0" />
          <div className="flex-1 min-w-0 space-y-2">
            <SkeletonLine className="w-[45%]" />
            <SkeletonLine size="xs" className="w-[65%]" />
          </div>
          <div className="flex flex-col items-end gap-2 shrink-0">
            <SkeletonLine size="lg" className="w-16" />
            <SkeletonLine size="xs" className="w-10" />
          </div>
        </SkeletonCard>
      ))}
    </div>
  );
}
